import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Wrapper from '../../components/Wrapper'
import { Paper, Accordion, AccordionSummary, Box, Tabs, Tab, Typography, TextField, List, ListItemButton, ListItemText, Divider, IconButton, Badge } from '@mui/material'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import TelegramIcon from '@mui/icons-material/Telegram'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { menu, graphics, telecomunication } from '../../translate'
import qs from 'query-string'
import { greenTheme, historyChatsStyles } from '../../helpers/style'
import '../../assets/style/style.css'
import { useSelector } from 'react-redux'
import { hasHistoryView, hasReportView, hasEmployeeTelegram, hasEmployeeWhatsApp, hasTelegramConnected, hasWhatsAppConnected } from '../../helpers/employeePermissions'
import Api from '../../Api'
import { toast } from 'react-toastify'

function normalizeDigits(v) {
  return String(v || '').replace(/[^\d]/g, '')
}

export default function WhatsappHistory() {
  const location = useLocation()
  const navigate = useNavigate()
  const query = qs.parse(location.search, { arrayFormat: 'comma' })
  const [expanded, setExpanded] = useState(true)
  const langIndex = +localStorage.getItem('atsLang') || 1
  const employeeData = useSelector((state) => state.users.employeeData)
  const [waAccounts, setWaAccounts] = useState([])
  const [telegramAccounts, setTelegramAccounts] = useState([])
  const [telegramIdForLink, setTelegramIdForLink] = useState(null)
  const showCallHistoryNav = hasHistoryView(employeeData)
  const showReportNav = hasReportView(employeeData)
  const showTelegramNav = hasTelegramConnected(employeeData, {
    ursTelegramAccountsCount: hasEmployeeTelegram(employeeData) ? 0 : telegramAccounts.length,
  })
  const showWhatsappNav = hasWhatsAppConnected(employeeData, {
    ursWhatsAppAccountsCount: hasEmployeeWhatsApp(employeeData) ? 0 : waAccounts.length,
  })
  const [selectedWaId, setSelectedWaId] = useState(query.wa || null)
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [search, setSearch] = useState(String(query.to || ''))
  const [selectedPhone, setSelectedPhone] = useState(query.to ? normalizeDigits(query.to) : '')
  const [conversationId, setConversationId] = useState(query.to ? normalizeDigits(query.to) : '')
  const [conversations, setConversations] = useState([])
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [messages, setMessages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sending, setSending] = useState(false)
  const [draft, setDraft] = useState('')
  const [pickedFile, setPickedFile] = useState(null)
  const fileInputRef = useRef(null)
  const threadEndRef = useRef(null)
  const loadingConversationsRef = useRef(false)
  const loadingMessagesRef = useRef(false)
  const conversationsSigRef = useRef('')
  const lastAutoScrollAtRef = useRef(0)

  const pathname = location.pathname || ''
  const isHistoryActive = pathname === '/history'
  const isOperatorReportActive = pathname === '/operator_report' || pathname.startsWith('/operator_report/')
  const isTelegramActive = pathname.startsWith('/history_telegram')
  const isWhatsappActive = pathname.startsWith('/history_whatsapp')
  const primaryColor = greenTheme?.primary || '#7ebc0a'
  const whatsappGreen = '#25D366'

  useEffect(() => {
    if (hasEmployeeTelegram(employeeData)) {
      setTelegramIdForLink(employeeData.telegrams[0]?.id ?? null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await Api.telegramGet()
        const accounts = res?.data?.data || []
        const list = Array.isArray(accounts) ? accounts : []
        if (!cancelled) {
          setTelegramAccounts(list)
          setTelegramIdForLink(list.length > 0 ? list[0].id : null)
        }
      } catch (e) {
        if (!cancelled) {
          setTelegramAccounts([])
          setTelegramIdForLink(null)
        }
      }
    })()
    return () => { cancelled = true }
  }, [employeeData?.telegrams])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoadingAccounts(true)
        const res = await Api.whatsappGet()
        const rows = res?.data?.data || []
        if (!cancelled) setWaAccounts(Array.isArray(rows) ? rows : [])
      } catch (e) {
        if (!cancelled) setWaAccounts([])
      } finally {
        if (!cancelled) setLoadingAccounts(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const normalizedId = (obj) => (obj?.id != null ? String(obj.id) : obj?.ID != null ? String(obj.ID) : '')

  const employeeWhatsappRows = useMemo(() => {
    const fromPlural = employeeData?.whatsapps
    const fromSingular = employeeData?.whatsapp
    if (Array.isArray(fromPlural) && fromPlural.length) return fromPlural
    if (Array.isArray(fromSingular) && fromSingular.length) return fromSingular
    return []
  }, [employeeData?.whatsapps, employeeData?.whatsapp])

  const displayedAccounts = useMemo(() => {
    if (employeeWhatsappRows.length > 0) {
      return employeeWhatsappRows.map((w) => {
        const full = waAccounts.find((acc) => normalizedId(acc) === normalizedId(w))
        return full != null ? full : w
      })
    }
    return waAccounts
  }, [employeeWhatsappRows, waAccounts])

  useEffect(() => {
    if (selectedWaId) {
      const exists = displayedAccounts.some((a) => normalizedId(a) === String(selectedWaId))
      if (!exists && displayedAccounts.length) setSelectedWaId(displayedAccounts[0].id)
      return
    }
    if (displayedAccounts.length) setSelectedWaId(displayedAccounts[0].id)
  }, [displayedAccounts, selectedWaId])

  const refreshConversations = useCallback(async () => {
    if (!selectedWaId) {
      setConversations([])
      return
    }
    try {
      setLoadingConversations(true)
      loadingConversationsRef.current = true
      const params = { wa_phone_row_id: selectedWaId, limit: 200 }
      if (search) params.q = search
      const { data } = await Api.whatsappConversationsGet(params)
      const next = Array.isArray(data?.data) ? data.data : []
      // Avoid re-render loops when backend polling returns identical data.
      const sig = next
        .map((c) => `${c.conversation_key || c.conversation_id || c.partner_phone || ''}|${c.last_message_time || ''}|${c.last_text || ''}|${c.total_messages || ''}`)
        .join('~')
      if (sig !== conversationsSigRef.current) {
        conversationsSigRef.current = sig
        setConversations(next)
      }
    } catch {
      setConversations([])
    } finally {
      setLoadingConversations(false)
      loadingConversationsRef.current = false
    }
  }, [selectedWaId, search])

  // Poll conversation list while the WhatsApp tab is open.
  useEffect(() => {
    if (!selectedWaId) return undefined
    refreshConversations()
    const t = setInterval(() => {
      if (loadingConversationsRef.current) return
      refreshConversations()
    }, 20000)
    return () => clearInterval(t)
  }, [selectedWaId, refreshConversations])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!selectedWaId || !conversationId) {
        setMessages([])
        return
      }
      try {
        setLoadingMessages(true)
        loadingMessagesRef.current = true
        const { data } = await Api.whatsappConversationGet(conversationId, {
          wa_phone_row_id: selectedWaId,
          limit: 500,
          page: 1,
        })
        if (!cancelled) setMessages(Array.isArray(data?.data) ? data.data : [])
      } catch {
        if (!cancelled) setMessages([])
      } finally {
        if (!cancelled) setLoadingMessages(false)
        loadingMessagesRef.current = false
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedWaId, conversationId])

  // Mark conversation as read (best-effort) when opening it.
  useEffect(() => {
    if (!selectedWaId || !conversationId) return
    ;(async () => {
      try {
        await Api.whatsappMarkConversationRead(conversationId, { wa_phone_row_id: selectedWaId })
        // Refresh conversation list so per-chat unread badges drop immediately.
        refreshConversations()
      } catch {}
    })()
  }, [selectedWaId, conversationId])

  const refreshConversationMessages = useCallback(async () => {
    if (!selectedWaId || !conversationId) return
    loadingMessagesRef.current = true
    try {
      const { data } = await Api.whatsappConversationGet(conversationId, {
        wa_phone_row_id: selectedWaId,
        limit: 200,
        page: 1,
      })
      const next = Array.isArray(data?.data) ? data.data : []
      setMessages((prev) => {
        const prevArr = Array.isArray(prev) ? prev : []
        // Deduplicate by meta_message_id if present, otherwise by id.
        const keyOf = (m) => String(m?.meta_message_id || m?.id || '')
        const seen = new Set(prevArr.map((m) => keyOf(m)).filter(Boolean))

        const newOnes = []
        for (const m of next) {
          const k = keyOf(m)
          if (k && seen.has(k)) continue
          newOnes.push(m)
          if (k) seen.add(k)
        }

        // If nothing new arrived, return same reference to avoid re-render flicker.
        if (newOnes.length === 0) return prevArr

        const merged = [...prevArr, ...newOnes]

        // Usually new webhook rows are newer, so appending avoids re-order flicker.
        const prevLastTime = prevArr.reduce((acc, m) => {
          const t = m?.message_time ? new Date(m.message_time).getTime() : null
          return t != null && t > acc ? t : acc
        }, -Infinity)
        const allNewAreNewerOrEqual = newOnes.every((m) => {
          const t = m?.message_time ? new Date(m.message_time).getTime() : null
          return t == null ? true : t >= prevLastTime
        })

        if (allNewAreNewerOrEqual) return merged

        // Fallback: keep stable ordering by backend time+id when out-of-order inserts happen.
        merged.sort((a, b) => {
          const at = a?.message_time ? new Date(a.message_time).getTime() : 0
          const bt = b?.message_time ? new Date(b.message_time).getTime() : 0
          if (at !== bt) return at - bt
          return Number(a?.id || 0) - Number(b?.id || 0)
        })

        return merged
      })
    } catch {
      // best-effort polling; ignore errors to avoid UI flicker
    } finally {
      loadingMessagesRef.current = false
    }
  }, [selectedWaId, conversationId])

  // Poll messages while the conversation is open.
  useEffect(() => {
    if (!selectedWaId || !conversationId) return undefined
    refreshConversationMessages()
    const t = setInterval(() => {
      // Skip polling when a manual fetch is already happening.
      if (loadingMessagesRef.current) return
      refreshConversationMessages()
    }, 15000)
    return () => clearInterval(t)
  }, [selectedWaId, conversationId, refreshConversationMessages])

  const openChatForNumber = (rawNumber) => {
    if (!selectedWaId) return
    const p = normalizeDigits(rawNumber)
    if (!p) return
    setSelectedPhone(p)
    setConversationId(p)
    const nextQuery = { ...query, wa: String(selectedWaId), to: p }
    navigate(`/history_whatsapp?${qs.stringify(nextQuery, { arrayFormat: 'comma' })}`)
  }

  const openConversation = (row) => {
    const p = normalizeDigits(row?.partner_phone || row?.contact_wa_id || row?.from_phone || row?.to_phone || '')
    const key = String(row?.conversation_key || row?.conversation_id || p)
    if (!key) return
    setConversationId(key)
    setSelectedPhone(p || key)
    const nextQuery = { ...query, wa: String(selectedWaId), to: p || key }
    navigate(`/history_whatsapp?${qs.stringify(nextQuery, { arrayFormat: 'comma' })}`)
  }

  const handleSendMessage = async () => {
    const text = String(draft || '').trim()
    if (!selectedWaId || !selectedPhone || (!text && !pickedFile)) return
    try {
      setSending(true)
      if (pickedFile) {
        await Api.smsSendWhatsAppMedia({
          wa_phone_id: selectedWaId,
          to: selectedPhone,
          caption: text,
          file: pickedFile,
        })
      } else {
        await Api.smsSendWhatsAppText({
          wa_phone_id: selectedWaId,
          to: selectedPhone,
          body: text,
        })
      }
      setDraft('')
      setPickedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (conversationId) {
        const { data } = await Api.whatsappConversationGet(conversationId, {
          wa_phone_row_id: selectedWaId,
          limit: 500,
          page: 1,
        })
        setMessages(Array.isArray(data?.data) ? data.data : [])
      }
      const cRes = await Api.whatsappConversationsGet({ wa_phone_row_id: selectedWaId, q: search, limit: 500 })
      setConversations(Array.isArray(cRes?.data?.data) ? cRes.data.data : [])
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const filteredContacts = useMemo(() => conversations, [conversations])

  useEffect(() => {
    const now = Date.now()
    // Throttle auto-scroll to avoid noticeable UI jumps during bursts of messages.
    if (now - lastAutoScrollAtRef.current < 800) return
    lastAutoScrollAtRef.current = now
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [selectedWaId, selectedPhone, messages.length])

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ minWidth: 'fit-content', fontSize: 10, boxShadow: 'none', borderRadius: 0 }}>
          <Accordion
            expanded={expanded}
            sx={{
              borderRadius: '0 !important',
              '&:before': { borderRadius: 0 },
              '&:first-of-type': { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
              '&:last-of-type': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '& .MuiAccordionSummary-root': { borderRadius: 0 },
              '& .MuiAccordionDetails-root': { borderRadius: 0 },
            }}
          >
            <AccordionSummary
              sx={{ ...historyChatsStyles.accordionSummary, borderRadius: 0 }}
              aria-expanded="true"
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <div className="w-100" style={historyChatsStyles.headerContainer}>
                <div style={historyChatsStyles.headerRow}>
                  {showCallHistoryNav ? (
                  <>
                  <h1 style={historyChatsStyles.headerTitle}>
                    <Link
                      to={`/history?${qs.stringify(query, { arrayFormat: 'comma' })}`}
                      style={{ color: isHistoryActive ? primaryColor : undefined, textDecoration: 'none' }}
                    >
                      {menu.history[langIndex]}
                    </Link>
                  </h1>
                  </>
                  ) : null}
                  {showReportNav ? (
                  <>
                  {showCallHistoryNav ? <div className="header_border" style={{ height: 40, marginBottom: 15 }} /> : null}
                  <h1 style={historyChatsStyles.headerTitle}>
                    <Link
                      to="/operator_report"
                      onClick={() => navigate('/operator_report')}
                      style={{ color: isOperatorReportActive ? primaryColor : undefined, textDecoration: 'none' }}
                    >
                      {graphics.graphic[langIndex]}
                    </Link>
                  </h1>
                  </>
                  ) : null}
                  {showTelegramNav && telegramIdForLink ? (
                  <>
                  {(showCallHistoryNav || showReportNav) ? <div className="header_border" style={{ height: 40, marginBottom: 15 }} /> : null}
                  <div style={{ ...historyChatsStyles.headerRow, gap: 8 }}>
                    <h1 style={historyChatsStyles.headerTitle}>
                      <Link
                        to={`/history_telegram/${telegramIdForLink}`}
                        style={{ color: isTelegramActive ? '#0088cc' : undefined, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <TelegramIcon sx={{ fontSize: 22, color: isTelegramActive ? '#0088cc' : 'inherit' }} />
                        Telegram
                      </Link>
                    </h1>
                  </div>
                  </>
                  ) : null}
                  {showWhatsappNav ? (
                  <>
                  <div className="header_border" style={{ height: 40, marginBottom: 15 }} />
                  <div style={{ ...historyChatsStyles.headerRow, gap: 8 }}>
                    <h1 style={historyChatsStyles.headerTitle}>
                      <Link
                        to="/history_whatsapp"
                        style={{ color: isWhatsappActive ? whatsappGreen : undefined, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        <WhatsAppIcon sx={{ fontSize: 22, color: isWhatsappActive ? whatsappGreen : 'inherit' }} />
                        {telecomunication.whatsapp_title?.[langIndex] || 'WhatsApp'}
                      </Link>
                    </h1>
                  </div>
                  </>
                  ) : null}
                </div>

                <span style={{ display: 'flex', alignItems: 'center' }} />
              </div>
            </AccordionSummary>
          </Accordion>
        </Paper>

        {displayedAccounts.length > 0 && (
          <Paper sx={{ mt: 1, borderRadius: 0, boxShadow: 'none' }}>
            <Tabs
              value={selectedWaId != null ? String(selectedWaId) : false}
              onChange={(_, v) => {
                setSelectedWaId(v)
                setConversationId('')
                setSelectedPhone('')
                const nextQuery = { ...query, wa: String(v) }
                delete nextQuery.to
                navigate(`/history_whatsapp?${qs.stringify(nextQuery, { arrayFormat: 'comma' })}`)
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: '1px solid #e5e7eb',
                '& .MuiTab-root': { textTransform: 'none' },
                '& .Mui-selected': { color: `${whatsappGreen} !important` },
                '& .MuiTabs-indicator': { backgroundColor: whatsappGreen, height: 3 },
              }}
            >
              {displayedAccounts.map((acc) => {
                const title = acc?.wa_title || acc?.wa_phone_number || `#${acc?.id}`
                return (
                  <Tab
                    key={acc.id ?? title}
                    value={String(acc.id)}
                    icon={<WhatsAppIcon sx={{ fontSize: 20 }} />}
                    iconPosition="start"
                    label={title}
                  />
                )
              })}
            </Tabs>
          </Paper>
        )}

        <Paper sx={{ mt: 1, minHeight: 520, borderRadius: 0, boxShadow: 'none' }}>
          {loadingAccounts ? (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: 'text.secondary' }}>Loading WhatsApp accounts…</Typography>
            </Box>
          ) : displayedAccounts.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ color: 'text.secondary' }}>
                {telecomunication.whatsapp_no_numbers?.[langIndex] || 'No WhatsApp numbers connected.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', height: 520, minHeight: 520 }}>
              {/* Left: contacts OR search-only */}
              <Box sx={{ width: 320, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search / type number"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') openChatForNumber(search)
                    }}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />,
                    }}
                  />
                </Box>
                {loadingConversations ? (
                  <Box sx={{ px: 2, pb: 2, color: 'text.secondary', fontSize: 13 }}>
                    Loading conversations...
                  </Box>
                ) : filteredContacts.length === 0 ? (
                  <Box sx={{ px: 2, pb: 2, color: 'text.secondary', fontSize: 13 }}>
                    No contacts yet. Type a number above to start chatting.
                  </Box>
                ) : (
                  <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
                    {filteredContacts.map((c) => {
                      const p = normalizeDigits(c.partner_phone || c.contact_wa_id || '')
                      const key = String(c.conversation_key || c.conversation_id || p)
                      const isActive = conversationId && String(conversationId) === key
                      const unread = Number(c?.unread_count || 0)
                      return (
                        <React.Fragment key={key}>
                          <ListItemButton
                            selected={isActive}
                            onClick={() => openConversation(c)}
                            sx={{
                              '&.Mui-selected': { backgroundColor: `${whatsappGreen}12` },
                              '&.Mui-selected:hover': { backgroundColor: `${whatsappGreen}18` },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                              <ListItemText
                                primary={c.contact_name || p || key}
                                secondary={c.last_text || p || ''}
                                primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                                secondaryTypographyProps={{ fontSize: 12 }}
                              />
                              {unread > 0 && (
                                <Badge
                                  badgeContent={unread}
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      right: 6,
                                      backgroundColor: whatsappGreen,
                                      color: '#fff',
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          </ListItemButton>
                          <Divider />
                        </React.Fragment>
                      )
                    })}
                  </List>
                )}
              </Box>

              {/* Right: chat panel */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon sx={{ color: whatsappGreen }} />
                  <Typography sx={{ fontWeight: 700 }}>
                    {selectedPhone ? selectedPhone : 'New chat'}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#fafafa' }}>
                  {!selectedPhone ? (
                    <Typography sx={{ color: 'text.secondary' }}>
                      Type a number on the left to open chat.
                    </Typography>
                  ) : loadingMessages ? (
                    <Typography sx={{ color: 'text.secondary' }}>
                      Loading messages...
                    </Typography>
                  ) : messages.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary' }}>
                      No messages yet. Send a message to start.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {messages.map((m) => (
                        <Box
                          key={m.id}
                          sx={{
                            alignSelf: String(m.direction || '').toLowerCase() === 'out' ? 'flex-end' : 'flex-start',
                            maxWidth: '75%',
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: String(m.direction || '').toLowerCase() === 'out' ? `${whatsappGreen}22` : '#fff',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                            {m.text_body || (m.media_filename ? `[${m.message_type}] ${m.media_filename}` : `[${m.message_type}]`)}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5, textAlign: 'right' }}>
                            {m.message_time ? new Date(m.message_time).toLocaleString() : ''}
                          </Typography>
                        </Box>
                      ))}
                      <div ref={threadEndRef} />
                    </Box>
                  )}
                </Box>

                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleSendMessage()
                  }}
                  sx={{ p: 1.5, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 1, alignItems: 'center' }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={selectedPhone ? 'Type message (optional with file)…' : 'Select / open a chat first'}
                    disabled={!selectedPhone || sending}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
                  />
                  <IconButton
                    type="button"
                    disabled={!selectedPhone || sending}
                    onClick={() => fileInputRef.current?.click()}
                    title={pickedFile ? pickedFile.name : 'Attach file/audio'}
                  >
                    <AttachFileIcon sx={{ color: selectedPhone ? whatsappGreen : 'text.disabled' }} />
                  </IconButton>
                  <IconButton type="submit" disabled={!selectedPhone || sending || (!String(draft || '').trim() && !pickedFile)}>
                    <SendIcon sx={{ color: selectedPhone ? whatsappGreen : 'text.disabled' }} />
                  </IconButton>
                </Box>
                {pickedFile ? (
                  <Box sx={{ px: 1.5, pb: 1, fontSize: 12, color: 'text.secondary' }}>
                    Attached: {pickedFile.name}
                  </Box>
                ) : null}
              </Box>
            </Box>
          )}
        </Paper>
      </div>
    </Wrapper>
  )
}
